"use client";

import React from "react";
import { useGraphStore } from "@/store/graph-store";
import { LensSelector } from "./lens-selector";
import { SaveViewDialog } from "./save-view-dialog";
import { Button } from "@/components/ui/button";
import {
  Target,
  Users,
  FolderGit,
  Landmark,
  Truck,
  ShieldAlert,
  FileText,
  Lightbulb,
  Maximize2,
  ZoomIn,
  ZoomOut,
  LayoutGrid,
  CircleDot,
  Trash2,
  RefreshCw,
} from "lucide-react";
import { cn } from "@/lib/utils";

const categories = [
  {
    id: "strategy",
    label: "Strategy",
    icon: Target,
    activeClass:
      "bg-category-strategy text-white border-transparent hover:opacity-90",
  },
  {
    id: "people",
    label: "People",
    icon: Users,
    activeClass:
      "bg-category-people text-white border-transparent hover:opacity-90",
  },
  {
    id: "work",
    label: "Work",
    icon: FolderGit,
    activeClass:
      "bg-category-work text-white border-transparent hover:opacity-90",
  },
  {
    id: "finance",
    label: "Finance",
    icon: Landmark,
    activeClass:
      "bg-category-finance text-white border-transparent hover:opacity-90",
  },
  {
    id: "operations",
    label: "Operations",
    icon: Truck,
    activeClass:
      "bg-category-operations text-white border-transparent hover:opacity-90",
  },
  {
    id: "risk",
    label: "Risk",
    icon: ShieldAlert,
    activeClass:
      "bg-category-risk text-white border-transparent hover:opacity-90",
  },
  {
    id: "documents",
    label: "Docs",
    icon: FileText,
    activeClass:
      "bg-category-documents text-white border-transparent hover:opacity-90",
  },
  {
    id: "innovation",
    label: "Innovation",
    icon: Lightbulb,
    activeClass:
      "bg-category-innovation text-white border-transparent hover:opacity-90",
  },
];

export function GraphToolbar() {
  const { visibleCategories, applyCategoryFilter, allNodes } = useGraphStore();

  const handleToggleCategory = (catId: string) => {
    const updated = visibleCategories.includes(catId)
      ? visibleCategories.filter((c) => c !== catId)
      : [...visibleCategories, catId];
    applyCategoryFilter(updated);
  };

  const handleZoomFit = () => {
    const fitBtn = document.querySelector(
      ".react-flow__controls-fitview",
    ) as HTMLButtonElement | null;
    if (fitBtn) fitBtn.click();
  };

  const handleZoomIn = () => {
    const zoomInBtn = document.querySelector(
      ".react-flow__controls-zoomin",
    ) as HTMLButtonElement | null;
    if (zoomInBtn) zoomInBtn.click();
  };

  const handleZoomOut = () => {
    const zoomOutBtn = document.querySelector(
      ".react-flow__controls-zoomout",
    ) as HTMLButtonElement | null;
    if (zoomOutBtn) zoomOutBtn.click();
  };

  const handleCleanCanvas = () => {
    applyCategoryFilter([]);
  };

  const handleResetGraph = () => {
    applyCategoryFilter(["strategy", "people", "risk"]);
    setTimeout(handleZoomFit, 100);
  };

  const triggerLayout = (layoutType: "circle" | "grid") => {
    if (allNodes.length === 0) return;

    let newNodes = [...allNodes];

    if (layoutType === "circle") {
      // Dynamically calculate radius to prevent overlapping cards (each card is ~224px wide)
      const nodeCount = allNodes.length;
      const spacing = 320; // 224px width + 96px gap
      const circumference = nodeCount * spacing;
      const radius = Math.max(350, circumference / (2 * Math.PI));

      // Center the large circle canvas
      const centerX = radius + 100;
      const centerY = radius + 100;

      newNodes = allNodes.map((node, idx) => {
        const angle = (idx / nodeCount) * 2 * Math.PI;
        return {
          ...node,
          position: {
            x: centerX + Math.cos(angle) * radius,
            y: centerY + Math.sin(angle) * radius,
          },
        };
      });
    } else if (layoutType === "grid") {
      const cols = Math.ceil(Math.sqrt(allNodes.length));
      const spacingX = 320; // spacious 96px gap between card widths
      const spacingY = 240; // spacious 100px gap between card heights
      newNodes = allNodes.map((node, idx) => {
        const row = Math.floor(idx / cols);
        const col = idx % cols;
        return {
          ...node,
          position: {
            x: col * spacingX + 150,
            y: row * spacingY + 150,
          },
        };
      });
    }

    useGraphStore.setState({ allNodes: newNodes });
    applyCategoryFilter(visibleCategories);

    setTimeout(handleZoomFit, 100);
  };

  return (
    <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-40 w-[95%] max-w-5xl bg-card/95 backdrop-blur-md border border-border shadow-xl rounded-xl px-4 py-3 flex flex-wrap items-center justify-between gap-4 select-none animate-in fade-in slide-in-from-bottom-4 duration-300">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[10px] font-bold uppercase tracking-wider text-text-muted mr-1.5">
          Filtri Categoria
        </span>
        {categories.map((cat) => {
          const CatIcon = cat.icon;
          const isActive = visibleCategories.includes(cat.id);
          return (
            <button
              key={cat.id}
              onClick={() => handleToggleCategory(cat.id)}
              className={cn(
                "h-7 px-2.5 rounded-full border text-[11px] font-semibold flex items-center gap-1.5 transition-all cursor-pointer",
                isActive
                  ? cat.activeClass
                  : "bg-card-muted/50 border-border text-text-secondary hover:bg-card-muted",
              )}
            >
              <CatIcon className="h-3.5 w-3.5" />
              {cat.label}
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-4 flex-wrap">
        <LensSelector />

        <div className="flex flex-col gap-1">
          <span className="text-[10px] font-bold uppercase tracking-wider text-text-muted">
            Disposizione
          </span>
          <div className="flex items-center gap-1 bg-card-muted/50 p-0.5 border border-border rounded-lg">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => triggerLayout("circle")}
              className="h-7 px-2 text-[10px] font-semibold flex items-center gap-1 hover:bg-card cursor-pointer"
              title="Disposizione a Cerchio"
            >
              <CircleDot className="h-3 w-3" />
              Cerchio
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => triggerLayout("grid")}
              className="h-7 px-2 text-[10px] font-semibold flex items-center gap-1 hover:bg-card cursor-pointer"
              title="Disposizione a Griglia"
            >
              <LayoutGrid className="h-3 w-3" />
              Griglia
            </Button>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-[10px] font-bold uppercase tracking-wider text-text-muted">
            Zoom
          </span>
          <div className="flex items-center border border-border rounded-lg overflow-hidden bg-card">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleZoomIn}
              className="h-7 w-7 rounded-none border-r border-border hover:bg-card-muted cursor-pointer"
              title="Zoom In"
            >
              <ZoomIn className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleZoomOut}
              className="h-7 w-7 rounded-none border-r border-border hover:bg-card-muted cursor-pointer"
              title="Zoom Out"
            >
              <ZoomOut className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleZoomFit}
              className="h-7 w-7 rounded-none hover:bg-card-muted cursor-pointer"
              title="Zoom to Fit"
            >
              <Maximize2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-[10px] font-bold uppercase tracking-wider text-text-muted">
            Azioni
          </span>
          <div className="flex items-center border border-border rounded-lg overflow-hidden bg-card p-0.5 gap-0.5">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCleanCanvas}
              className="h-7 px-2 text-[10px] font-semibold flex items-center gap-1 hover:bg-card-muted text-error hover:text-error cursor-pointer"
              title="Pulisci tutti gli elementi"
            >
              <Trash2 className="h-3 w-3" />
              Pulisci
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleResetGraph}
              className="h-7 px-2 text-[10px] font-semibold flex items-center gap-1 hover:bg-card-muted cursor-pointer"
              title="Ripristina vista di default"
            >
              <RefreshCw className="h-3 w-3 text-text-secondary" />
              Reset
            </Button>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-[10px] font-bold uppercase tracking-wider text-text-transparent select-none">
            Viste
          </span>
          <SaveViewDialog />
        </div>
      </div>
    </div>
  );
}
